import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Building2, FileText, Calendar, Lightbulb } from 'lucide-react';

const proServices = [
  {
    id: 1,
    title: "DTG",
    subtitle: "Diagnostic Technique Global",
    description: "Analyse complète de l'état des parties communes et équipements collectifs de votre copropriété. Obligatoire pour les immeubles de plus de 10 ans.",
    icon: Building2,
    badge: "Copropriétés"
  },
  {
    id: 2,
    title: "DPE Collectif",
    subtitle: "Performance Énergétique",
    description: "Évaluation de la consommation énergétique globale de l'immeuble et recommandations d'amélioration pour les parties communes.",
    icon: Lightbulb,
    badge: "Obligatoire"
  },
  {
    id: 3,
    title: "PPT",
    subtitle: "Plan Pluriannuel de Travaux",
    description: "Planification des travaux nécessaires sur 10 ans pour préserver et améliorer votre immeuble collectif.",
    icon: Calendar,
    badge: "Anticipation"
  },
  {
    id: 4,
    title: "Diagnostics Parties Communes",
    subtitle: "Amiante, Plomb, Électricité",
    description: "Diagnostics réglementaires des parties communes : amiante avant travaux, plomb, installations électriques et gaz collectifs.",
    icon: FileText,
    badge: "Sécurité"
  }
];

export const ProServices = () => {
  return (
    <section id="pro-services" className="py-32 bg-gradient-to-br from-stone-900 via-stone-800 to-stone-900 text-white relative overflow-hidden">
      {/* Decorative background */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(52,211,153,0.1),transparent_70%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,rgba(52,211,153,0.05),transparent_70%)]" />
      
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16 space-y-6">
          <div className="inline-flex items-center gap-2 text-emerald-400 mb-4">
            <div className="w-12 h-[2px] bg-emerald-400" />
            <span className="text-sm uppercase tracking-widest font-medium">Services Professionnels</span>
            <div className="w-12 h-[2px] bg-emerald-400" />
          </div>
          
          <h2 className="text-5xl md:text-6xl font-bold text-white">
            Copropriétés & Professionnels
          </h2>
          
          <p className="text-xl text-stone-300 max-w-3xl mx-auto leading-relaxed">
            Solutions complètes pour les syndics, gestionnaires de copropriété et professionnels de l'immobilier
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {proServices.map((service, index) => {
            const Icon = service.icon;
            return (
              <Card 
                key={service.id}
                className="group hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-emerald-800/50 bg-white/5 backdrop-blur-sm hover:bg-white/10"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <CardHeader>
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-xl">
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <span className="px-3 py-1 rounded-full bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 text-xs font-bold uppercase tracking-wider">
                      {service.badge}
                    </span>
                  </div>
                  <CardTitle className="text-3xl font-bold text-white group-hover:text-emerald-400 transition-colors">
                    {service.title}
                  </CardTitle>
                  <CardDescription className="text-emerald-400 font-medium text-lg">
                    {service.subtitle}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-stone-300 leading-relaxed">
                    {service.description}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* CTA Section */}
        <div className="mt-16 text-center">
          <div className="inline-block bg-gradient-to-r from-emerald-600 to-emerald-500 rounded-2xl p-8 shadow-2xl">
            <h3 className="text-2xl font-bold text-white mb-3">
              Syndics & Gestionnaires
            </h3>
            <p className="text-white/90 mb-6 max-w-2xl">
              Tarifs préférentiels pour les professionnels. Intervention rapide et accompagnement sur-mesure pour vos copropriétés.
            </p>
            <a 
              href="#contact" 
              className="inline-block px-8 py-4 bg-white text-emerald-700 rounded-full font-bold hover:bg-stone-100 transition-all duration-300 hover:scale-105 shadow-lg"
            >
              Demander un devis professionnel
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};
